import React, { useContext, useEffect, useState } from "react";
import "./Sidebar.css";
import { Link, NavLink, useLocation, useSearchParams } from "react-router-dom";

import { translations } from "../../utils/lang";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import { visibleNavItems } from "../../access/core/routePolicies";
import {
  ChartPie,
  FileChartLine,
  Flag,
  LayoutList,
  Library,
  LogsIcon,
  Rocket,
  Sprout,
  Star,
  StoreIcon,
  TicketPercent,
  TrendingDown,
  UsersRound,
  Utensils,
  HeartHandshake,
  ChessKnight,
  Bell,
  Workflow,
  LayoutDashboard,
  ReceiptText,
  CreditCard,
  MessagesSquare,
  Mail as MailIcon,
  ChevronDown,
  Component,
} from "lucide-react";
import { fetchNotificationsAPI } from "../../utils/api/notifications";
import { useCopilotUnresolved } from "../copilotAdmin/useCopilotUnresolved";
import {
  REPORT_VIEWS,
  REPORT_VIEW_PARAM,
  getReportView,
} from "../reports/reportViews";
import { FILTER_KEYS } from "../../utils/reportFilters";
import {
  PAGE_SECTIONS,
  resolveSection,
  visibleSections,
} from "../../navigation/pageSections";

const NOTIFICATION_COUNT_EVENT = "lud-notification-count-change";

// Switching report view keeps the selected reporting period; other sections start clean.
const subnavParams = (param, item, current) => {
  const next = { [param]: item };
  if (param === REPORT_VIEW_PARAM) {
    const from = current.get(FILTER_KEYS.from);
    const to = current.get(FILTER_KEYS.to);
    if (from && to) Object.assign(next, { [FILTER_KEYS.from]: from, [FILTER_KEYS.to]: to });
  }
  return next;
};

export const Sidebar = () => {
  const { lang, storeAccess, branchAccess } = useContext(Context);
  const { can, has, isAccessReady, needsBranchAssignment } = useAccess();

  const [isSelected, setIsSelected] = useState("");
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  // Unresolved Copilot conversations (open + pending), from the backend. Only
  // requested for roles that hold copilot.nav; nobody else triggers a request.
  const canSeeCopilot = isAccessReady && can("copilot.nav");
  // Roles without the notification permission (the restricted `new` onboarding client) must not call the notification API: it answers 403 and
  // the global guard would show a "no permission" message on every page load.
  const canReadNotifications = isAccessReady && can("notification.view");
  const unresolvedCopilot = useCopilotUnresolved(canSeeCopilot);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeReportView = getReportView(searchParams);

  // The submenu of the open page can be collapsed by clicking its parent;
  // opening another page expands its submenu again.
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    setIsSelected(path);
  }, [location]);

  useEffect(() => {
    setCollapsed(false);
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const loadUnreadNotificationsCount = async () => {
      const token = localStorage.getItem("auth-token");

      if (!token || !canReadNotifications) {
        if (isMounted) setUnreadNotificationsCount(0);
        return;
      }

      try {
        const notifications = await fetchNotificationsAPI(
          token,
          storeAccess,
          branchAccess,
          {},
        );

        if (isMounted) {
          setUnreadNotificationsCount(
            Array.isArray(notifications)
              ? notifications.filter((item) => item.unread).length
              : 0,
          );
        }
      } catch {
        if (isMounted) setUnreadNotificationsCount(0);
      }
    };

    const handleNotificationCountChange = (event) => {
      const nextCount = Number(event?.detail?.unreadCount);

      if (Number.isFinite(nextCount)) {
        setUnreadNotificationsCount(Math.max(0, nextCount));
      } else {
        loadUnreadNotificationsCount();
      }
    };

    loadUnreadNotificationsCount();
    window.addEventListener(
      NOTIFICATION_COUNT_EVENT,
      handleNotificationCountChange,
    );

    return () => {
      isMounted = false;
      window.removeEventListener(
        NOTIFICATION_COUNT_EVENT,
        handleNotificationCountChange,
      );
    };
  }, [storeAccess, branchAccess, canReadNotifications]);

  /** ✅ DEFINE ALL POSSIBLE SIDEBAR ITEMS HERE */
  const allItems = {
    restaurants: {
      icon: <StoreIcon />,
      url: "restaurants",
    },
    "lud-dashboard": {
      icon: <LayoutDashboard />,
      url: "lud-dashboard",
    },
    menu: {
      icon: <Utensils />,
      url: "menu",
    },
    campaignRecord: {
      icon: <TicketPercent />,
      url: "campaignRecord",
    },
    operation: {
      icon: <Sprout />,
      url: "operation",
    },
    faults: {
      icon: <TrendingDown />,
      url: "faults",
    },
    reports: {
      icon: <LayoutList />,
      url: "reports",
    },
    "saled-items": {
      icon: <Star />,
      url: "saled-items",
    },
    analysis: {
      icon: <ChartPie />,
      url: "analysis",
    },
    "lud-documents": {
      icon: <FileChartLine />,
      url: "lud-documents",
    },
    notifications: {
      icon: <Bell />,
      url: "notifications",
    },
    support: {
      icon: <HeartHandshake />,
      url: "support",
    },
    copilot: {
      icon: <MessagesSquare />,
      url: "copilot",
    },
    guidelines: {
      icon: <Flag />,
      url: "guidelines",
    },
    onboarding: {
      icon: <Rocket />,
      url: "onboarding",
    },
    crm: {
      icon: <Library />,
      url: "crm",
    },
    "store-onboarding-admin": {
      icon: <ChessKnight />,
      url: "store-onboarding-admin",
    },
    "lud-connect": {
      icon: <Workflow />,
      url: "lud-connect",
    },
    lud: {
      icon: <FileChartLine />,
      url: "lud",
    },
    users: {
      icon: <UsersRound />,
      url: "users",
    },
    logs: {
      icon: <LogsIcon />,
      url: "logs",
    },
    "mail-inbox": {
      icon: <MailIcon />,
      url: "emails",
    },
    "delivery-documentation": {
      icon: <ReceiptText />,
      url: "delivery-documentation",
    },
    "delivery-documentation-admin": {
      icon: <ReceiptText />,
      url: "delivery-documentation-admin",
    },
    "billing": {
      icon: <CreditCard />,
      url: "billing",
    },
    "test-ui": {
      icon: <Component />,
      url: "test-ui",
      path: "/test-ui",
    },
  };

  /** ✅ The sidebar shows exactly the routes the user may open
   *  (policies live in src/access/core/routePolicies.js). */
  const allowed = isAccessReady
    ? visibleNavItems(can).map((route) => route.id)
    : [];

  const getLabel = (key) =>
    translations[lang].sidebar[key] ||
    (key === "delivery-documentation"
      ? "توثيق الطلبات"
      : key === "delivery-documentation-admin"
        ? "إدارة التوثيق"
        : key);

  // Submenu of a sidebar entry: the report views (Faults / Daily / Monthly)
  // or the page sections in src/navigation/pageSections.js. The selection
  // lives in the query string so refresh, direct links and back/forward keep it.
  const getSubnav = (key) => {
    if (key === "reports") {
      return {
        param: REPORT_VIEW_PARAM,
        active: activeReportView,
        items: REPORT_VIEWS.map(({ key: view, labelIndex, Icon }) => ({
          key: view,
          Icon,
          label: translations[lang].reports.header[labelIndex],
        })),
      };
    }
    if (!PAGE_SECTIONS[key]) return null;
    const access = { can, has };
    const items = visibleSections(key, access).map(({ key: section, Icon, label }) => ({
      key: section,
      Icon,
      label: label(lang),
    }));
    // A single section needs no submenu.
    if (items.length < 2) return null;
    const { param } = PAGE_SECTIONS[key];
    return {
      param,
      active: resolveSection(key, searchParams.get(param), access),
      items,
    };
  };

  const renderSubnav = (key, subnav, renderItem) => (
    <div
      id={`sidebar-subnav-${key}`}
      className="sidebar-subnav"
      role="group"
      aria-label={getLabel(key)}
    >
      {subnav.items.map(({ key: item, label, Icon }) => {
        const selected = subnav.active === item;
        return renderItem({
          key: item,
          selected,
          label,
          content: (
            <div className={`sidebar-item sidebar-sub-item ${selected ? "selected" : ""}`}>
              <Icon aria-hidden="true" />
              <p>{label}</p>
            </div>
          ),
        });
      })}
    </div>
  );

  const chevron = (expanded) => (
    <ChevronDown
      className={`sidebar-chevron ${expanded ? "expanded" : ""}`}
      aria-hidden="true"
    />
  );

  const currentPath = location.pathname.split("/").pop();

  /** ✅ NORMAL SIDEBAR */
  return (
    <div className="sidebar">
      {allowed.map((key) => {
        const subnav = getSubnav(key);
        const isOpenPage = currentPath === allItems[key].url;
        const expanded = Boolean(subnav) && isOpenPage && !collapsed;
        return (
        <React.Fragment key={key}>
        <NavLink
          to={allItems[key].path || `/auth/${allItems[key].url}`}
          title={getLabel(key)}
          aria-expanded={subnav ? expanded : undefined}
          aria-controls={expanded ? `sidebar-subnav-${key}` : undefined}
          onClick={(event) => {
            // On the open page the parent only expands/collapses its submenu
            // (navigating would drop the selected section).
            if (subnav && isOpenPage) {
              event.preventDefault();
              setCollapsed((value) => !value);
            }
            setIsSelected(key);
          }}
        >
          <div
            className={`sidebar-item ${
              isSelected === key ? "selected" : ""
            }`}
          >
            {allItems[key].icon}
            <p>{getLabel(key)}</p>

            {key === "notifications" &&
            unreadNotificationsCount > 0 ? (
              <span className="sidebar-notification-badge">
                {unreadNotificationsCount > 99
                  ? "99+"
                  : unreadNotificationsCount}
              </span>
            ) : null}

            {key === "copilot" && unresolvedCopilot > 0 ? (
              <span
                className="sidebar-notification-badge"
                data-testid="copilot-unresolved-badge"
                aria-label={`${unresolvedCopilot} ${lang === "en" ? "unresolved conversations" : "محادثات غير محلولة"}`}
              >
                {unresolvedCopilot > 99 ? "99+" : unresolvedCopilot}
              </span>
            ) : null}

            {subnav ? chevron(expanded) : null}
          </div>
        </NavLink>
        {expanded &&
          renderSubnav(key, subnav, ({ key: item, selected, label, content }) => (
            <Link
              key={item}
              to={{
                pathname: `/auth/${allItems[key].url}`,
                search: `?${new URLSearchParams(subnavParams(subnav.param, item, searchParams))}`,
              }}
              title={label}
              aria-current={selected ? "page" : undefined}
            >
              {content}
            </Link>
          ))}
        </React.Fragment>
        );
      })}
      {isAccessReady && needsBranchAssignment ? (
        <p className="sidebar-assignment-note" role="note" data-testid="sidebar-branch-note">
          {lang === "en"
            ? "No branch is assigned to your account, so branch pages are hidden. Contact your administrator."
            : "لا يوجد فرع مرتبط بحسابك، لذلك صفحات الفرع مخفية. تواصل مع المسؤول."}
        </p>
      ) : null}
    </div>
  );
};
